import React, { useState, useEffect } from 'react';
import Pagination from '../Tools/Pagination';
import AreaModal from './modals/Area/AreaModal';
import axiosInstance from '../../../api';

const AreaTab = () => {
  const [areas, setAreas] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    area_name: '',
    location_id: '',
    risk_category: '',
  });
  const [editId, setEditId] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchAreas(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const filtered = areas.filter((area) =>
      area.area_name?.toLowerCase().includes(filter.toLowerCase())
    );
    setFilteredAreas(filtered);
  }, [filter, areas]);

  
  const fetchAreas = async (page) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/areas?page=${page}`);
      const areaData = response.data?.data || response.data || [];
      setAreas(areaData);
      setFilteredAreas(areaData);
      setTotalPages(response.data?.last_page || 1);
    } catch (error) {
    window.confirm('Failed to load areas.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await axiosInstance.put(`/areas/${editId}`, formData);
        window.confirm('Area updated successfully.');

      } else {
        await axiosInstance.post('/areas', formData);
        window.confirm('Area added successfully.');
      }
      fetchAreas(currentPage);
      setFormData({ area_name: '', location_id: '', risk_category: '' });
      setEditId(null);
      document.getElementById('close-area-modal')?.click(); // close modal
    } catch (error) {
      window.confirm('Failed to save area.');
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this area?');
    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(`/areas/${id}`);
      window.confirm('Area deleted successfully.');
      fetchAreas(currentPage);
    } catch (error) {
      window.confirm('Failed to delete area.');
      console.error('Error deleting area:', error);
    }
  };

  return (
    <div className="tab-pane active" id="area" role="tabpanel">
      {/* Alert Message */}
      {alert.message && (
        <div className={`alert alert-${alert.type} mt-3 mx-3`} role="alert">
          {alert.message}
        </div>
      )}

      {/* Filter & Add Button */}
      <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel d-flex flex-wrap align-items-center">
          <form className="col-lg-2 col-12 p-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </form>

          <button
            className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary waves-effect waves-light"
            type="button"
            data-bs-toggle="modal"
            data-bs-target="#bs-area-modal"
            id="bs-area"
            onClick={() => {
              setFormData({ area_name: '', location_id: '', risk_category: '' });
              setEditId(null);
            }}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Area
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-4">Loading Areas...</div>
      ) : (
        <>
          <div className="py-3 px-0">
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Area Name</h6></th>
                    <th><h6 className="fw-semibold mb-0">Location ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Risk Category</h6></th>
                    <th><h6 className="fw-semibold mb-0">Actions</h6></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAreas.length > 0 ? (
                    filteredAreas.map((area) => (
                      <tr key={area.id}>
                        <td>{area.id.toString().padStart(2, '0')}</td>
                        <td>{area.area_name}</td>
                        <td>{area.location_id}</td>
                        <td>{area.risk_category}</td>
                        <td>
                          <button
                            className="h4 me-3 border-0 bg-transparent"
                            onClick={() => {
                              setFormData({
                                area_name: area.area_name,
                                location_id: area.location_id,
                                risk_category: area.risk_category,
                              });
                              setEditId(area.id);
                            }}
                            data-bs-toggle="modal"
                            data-bs-target="#bs-area-modal"
                            id="bs-area"
                          >
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button
                            className="h4 border-0 bg-transparent"
                            onClick={() => handleDelete(area.id)}
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">No Area Found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal */}
          <AreaModal
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            editId={editId}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center my-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AreaTab;
